import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import { ModalConfirmComponent, ModalConfirmData } from 'app/shared/modal-confirm/modal-confirm.component';
import { Loggable } from 'app/utils/logging/loggable';
import SocialRoomSet from 'entity/SocialRoomSet';
import User from 'entity/User';

import { ScootaversePageService } from '../../services/scootaverse-page.service';
import SocialTheme from 'entity/SocialTheme';

@Component({
  selector: 'scootaverse-card',
  templateUrl: './scootaverse-card.component.html',
  styleUrls: ['./scootaverse-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScootaverseCardComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public roomSet?: SocialRoomSet;
  @Input() public attachPage?: boolean;
  @Input() public disableHover?: boolean;
  public roomSetSubtitle: string | undefined;

  @Output() public enterRoom = new EventEmitter<string>();
  @Output() public selectedRoom = new EventEmitter<string>();
  @Output() public triggerRefetch = new EventEmitter<void>();
  @Output() public triggerChangeName = new EventEmitter<string>();

  private user?: User;
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected editingName = false;
  protected newScootaverseName?: string = '';

  public constructor(
    private store: Store,
    private _toasterService: ToastrService,
    private dialog: MatDialog,
    private scootaversePageService: ScootaversePageService,
    private socialPageService: SocialPageService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.setDisplayName('ScootaversePageComponent');

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this.user = user;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.newScootaverseName = this.roomSet?.name;
  }

  protected onCardClick() {
    if (this.editingName) {
      return;
    }
    if (this.attachPage) {
      this.onSelectRoom(this.roomSet);
    } else {
      this.onEnterScootaverse();
    }
  }

  public get socialTheme(): SocialTheme | undefined {
    return this.scootaversePageService.getSocialTheme(this.roomSet);
  }

  public onEnterScootaverse(): void {
    if (this.editingName) {
      return;
    }
    if (this.roomSet?.socialRoomSetId) {
      this.scootaversePageService.onEnterScootaverse(this.roomSet.socialRoomSetId).catch((e) => {
        this.error('Error redirecting to room - ', e);
      });
    }
  }

  public onSelectRoom(roomSet?: SocialRoomSet): void {
    if (roomSet) {
      this.selectedRoom.emit(roomSet.socialRoomSetId);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public executeCopy(): void {
    if (this.roomSet && this.roomSet.socialRoomSetId) {
      this.scootaversePageService
        .copySocialRoomSet(this.roomSet)
        .then(() => {
          this.triggerRefetch.emit();
          this._toasterService.success('Scootaverse duplicated successfully!', 'Success');
        })
        .catch((err) => {
          this.error(err);
          this._toasterService.error('Error duplicating scootaverse!', 'Error');
        });
    }
  }

  public duplicateScootaverse(): void {
    const ref = this.dialog.open<ModalConfirmComponent, ModalConfirmData>(ModalConfirmComponent, {
      panelClass: 'duplicate-modal',
      data: {
        title: 'Duplicate Scootaverse?',
        text: '',
        customConfirmationButton: 'Duplicate',
        actionButtonFillColor: '#0C58C6',
        actionButtonColor: '#FFFFFF',
        customFillButton: true,
      },
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.executeCopy();
        }
      });
  }

  public onScootaverseShareLink(roomSet?: SocialRoomSet): void {
    if (!this.user) {
      this.error('onShareLink: User is undefined');
      return;
    }
    const room = roomSet?.socials ? roomSet.socials[0] : undefined;
    if (room) {
      this.socialPageService.onShareLink({ room, user: this.user });
    }
  }

  public onEditScootaverse(roomSet?: SocialRoomSet): void {
    if (roomSet && roomSet.socialRoomSetId) {
      this.scootaversePageService.onEnterScootaverse(roomSet.socialRoomSetId, true).catch((e) => {
        this.error('Error redirecting to room - ', e);
      });
    }
  }

  public onDeleteScootaverse(): void {
    if (this.roomSet && this.roomSet.socialRoomSetId) {
      this.scootaversePageService.onDeleteScootaverse(this.roomSet);
    }
  }

  protected startEditing(name?: string) {
    this.newScootaverseName = name;
    this.editingName = true;
  }

  protected async editScootaverseName() {
    this.editingName = false;

    if (!this.roomSet) {
      this.error('Unable to get the SocialRoomSetId');
      return;
    }

    if (!this.newScootaverseName) {
      this.error('Name is required');
      this.newScootaverseName = this.roomSet?.name;
      return;
    }

    this.triggerChangeName.emit(this.newScootaverseName);

    await this.scootaversePageService
      .updateSocialRoomSet({
        socialRoomSetId: this.roomSet.socialRoomSetId,
        name: this.newScootaverseName,
      })
      .catch((err) => this.error(err));
  }
}
